import React from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import pluralize from 'rebook/apps/utils/text/pluralize';
import { URLS } from 'rebook/apps/base/constants';

import PaymentsCheckoutSmsForm from './PaymentsCheckoutSmsForm';
import PaymentsCheckoutCardForm from './PaymentsCheckoutCardForm';
import PaymentsCheckoutPaypalForm from './PaymentsCheckoutPaypalForm';
import { paymentMethods } from '../constants';
import styles from './style.scss';


const PromoCodeRestrictionWarning = (props) => {
  const { campaign, priceForUser, redeemCodeDiscountAmount, redeemCodeDiscountPercent } = props;

  return (
    <React.Fragment>
      {redeemCodeDiscountAmount === null && (
        <div className={classNames('billetContainerWarning', styles.discount)}>
          <div className={styles.discountIcon}>😱</div>
          <div>
            {campaign.min_amount_order > 0 && priceForUser < campaign.min_amount_order
              ? (
                <small>
                  Этот промо-код может быть применен,
                  только если сумма вашего заказа более {campaign.min_amount_order} ₽
                </small>
              )
              : (
                <small>
                  Код на скидку не применяется к выбранным параметрам, но вы все равно можете оплатить заказ
                </small>
              )
            }
          </div>
        </div>
      )}
      {redeemCodeDiscountPercent !== null
        && campaign.max_percent_discount > 0
        && campaign.discount_type === 'percent'
        && campaign.discount > redeemCodeDiscountPercent
        // the user's personal discount does not match the campaign percent number
        // because there is a discount limit calculated for all discount sources (such as advert campaign)
        && (
          <small className={styles.maxDiscount}>Максимальная скидка {campaign.max_percent_discount}%</small>
        )}
    </React.Fragment>
  );
};

const PaymentsCheckoutForm = ({ payments }) => {
  const {
    redeemCode, userPrice, subscriptionType, subscriptionPeriod, paymentMethod, isAutoRebill, subscriptionTypes, isGift,
  } = payments;

  const subscriptionTitleTypes = {
    standard: 'Стандартная подписка',
    pro: 'Премиум-подписка',
  };

  const hasStandardSubscription = subscriptionTypes.standard.userIsActive;
  const hasPremiumSubscription = subscriptionTypes.pro.userIsActive;
  let isStandardSubscriptionLonger = (hasStandardSubscription && !hasPremiumSubscription)
    ? true
    : false;

  if (hasStandardSubscription && hasPremiumSubscription) {
    const standardSubscriptionTime = new Date(payments.subscriptionTypes.standard.userActiveTill).getTime();
    const premiumSubscriptionTime = new Date(payments.subscriptionTypes.pro.userActiveTill).getTime();

    isStandardSubscriptionLonger = standardSubscriptionTime > premiumSubscriptionTime;
  }

  const isSurcharge = subscriptionType === 'pro' && hasStandardSubscription
    && payments.subscriptionTypes.standard.userActiveTill && isStandardSubscriptionLonger;

  const subscriptionTitle = isSurcharge
    ? 'Доплата за премиум-подписку'
    : subscriptionTitleTypes[subscriptionType];

  const monthCount = parseInt(subscriptionPeriod, 10);

  const checkoutMethodForms = {
    [paymentMethods.card.slug]: (props) => <PaymentsCheckoutCardForm {...props} />,
    [paymentMethods.sms.slug]: (props) => <PaymentsCheckoutSmsForm {...props} />,
    [paymentMethods.paypal.slug]: (props) => <PaymentsCheckoutPaypalForm {...props} />,
  };

  const isOneMonthPro = subscriptionType === 'pro' && subscriptionPeriod === '1';
  const isOneMonthStandart = subscriptionType === 'standard' && subscriptionPeriod === '1';

  let shouldShowAutoRebill = isAutoRebill && !isGift;

  if (paymentMethod === 'sms' && !((isOneMonthStandart || isOneMonthPro) && !(userPrice.discountAmount > 0))) {
    shouldShowAutoRebill = false;
  }

  return (
    <div className="billetContainer">
      <h2>Вы покупаете</h2>
      <div className={styles.priceSection}>
        <div className={styles.subscriptionType}>
          {subscriptionTitle}, {monthCount}&nbsp;{pluralize(monthCount, 'месяц', 'месяца', 'месяцев')}
        </div>
        <span className={styles.advertPrice}>
          {isSurcharge
            ? userPrice.finalPrice
            : userPrice.fullPrice
          } ₽
        </span>
      </div>
      {userPrice.discountAmount > 0 && !isSurcharge && (
        <div className={styles.priceSection}>
          <span className={styles.subscriptionType}>Скидка</span>
          <span className={styles.advertPrice}>{userPrice.discountAmount} ₽</span>
        </div>
      )}
      <div>
        <div className={styles.total}>Итого к оплате:</div>
        <div className={classNames(styles.totalPrice, 'jest-payments-checkout-total-price')}>
          {userPrice.finalPrice} ₽
        </div>
      </div>
      <div>
        {checkoutMethodForms[paymentMethod]({ priceForUser: userPrice.finalPrice })}
      </div>
      {redeemCode.campaign && (
        <PromoCodeRestrictionWarning
          campaign={redeemCode.campaign}
          priceForUser={userPrice.advertPrice}
          redeemCodeDiscountAmount={userPrice.redeemCodeDiscountAmount}
          redeemCodeDiscountPercent={userPrice.redeemCodeDiscountPercent} />
      )}
      <div>
        {shouldShowAutoRebill && (
          <p className={styles.description}>
            В конце срока подписка продлится автоматически,
            и с вашего счета спишется стоимость следующего периода.
            Вы можете в любой момент отменить автопродление в настройках вашего профиля.
          </p>
        )}
        <p className={styles.description}>
          Оплачивая подписку, я принимаю{' '}
          <Link to={`${URLS.aboutOffer}#a4`}>условия оплаты, указанные в оферте</Link>
          {shouldShowAutoRebill && (
            <span>
              , и{' '}
              <Link to={`${URLS.aboutOffer}#a4.4`}>
                условия автоматического продления подписки на месяц вперед
              </Link>
            </span>
          )}.
        </p>
      </div>
    </div>
  );
};

PaymentsCheckoutForm.propTypes = {
  payments: PropTypes.object.isRequired,
};

function mapStateToProps(state) {
  return {
    payments: state.payments,
  };
}

export default connect(mapStateToProps)(PaymentsCheckoutForm);
